import { FlexBox, Pagination, Spinner } from '@dixa/lib-shared-habla';
import {
  SearchConversationEnriched,
  SearchUserEnriched,
} from 'lib-search-types';
import { User } from 'lib-shared-types-api';
import { MouseEvent, RefObject } from 'react';

import { AwayStatus } from 'apis/presence';
import {
  type ActiveFilters,
  hasActiveFilters,
} from 'components/teamPageNew/components/searchControls';

import { useScrollToTopOnPageChange } from '../hooks/useScrollToTopOnPageChange';

import styles from './teamPage.module.scss';
import { TeamTableEmptyState } from './teamTableEmptyState';
import { TeamTableLoading } from './teamTableLoading';
import { TeamTableRow } from './teamTableRow';

type TeamPageProps = {
  // Data
  members: readonly SearchUserEnriched[];
  memberCount: number | undefined;
  pageCount: number | undefined;
  loading: boolean;

  // Pagination
  page: number;
  setPage: (page: number) => void;

  // Filters
  activeFilters: ActiveFilters;
  clearFilters: () => void;

  // User state
  favorites: readonly string[];
  ownId: string | undefined;
  toggleFavorite: (isFavorite: boolean, agentId: string) => () => void;
  showFavorites: boolean;
  showUserModal: (user: Partial<User>) => void;
  awayStatuses: AwayStatus[];
  hasPresenceAccess: boolean;
  hasOrganizationMemberAccess: boolean;

  // Scroll
  tableWrapperRef: RefObject<HTMLDivElement>;

  expandedMemberId: string | null;
  onToggleExpand: (memberId: string) => (e: MouseEvent<HTMLElement>) => void;
  conversations: readonly SearchConversationEnriched[];
  conversationsLoading: boolean;
  twilioMakeInternalCall: (userId: string) => Promise<void>;
  isMicrophoneBlocked: boolean;
  hasConversationReplyAccess: boolean;
};

export const TeamPage = ({
  members,
  memberCount,
  pageCount,
  loading,
  page,
  setPage,
  activeFilters,
  clearFilters,
  favorites,
  ownId,
  toggleFavorite,
  showFavorites,
  showUserModal,
  awayStatuses,
  hasPresenceAccess,
  hasOrganizationMemberAccess,
  tableWrapperRef,
  expandedMemberId,
  onToggleExpand,
  conversations,
  conversationsLoading,
  twilioMakeInternalCall,
  isMicrophoneBlocked,
  hasConversationReplyAccess,
}: TeamPageProps): JSX.Element => {
  useScrollToTopOnPageChange(page, loading, members, tableWrapperRef);

  if (!loading && members.length === 0 && hasActiveFilters(activeFilters)) {
    return <TeamTableEmptyState onClearFilters={clearFilters} />;
  }

  return (
    <FlexBox flexDirection="column" className={styles.wrapper}>
      <div ref={tableWrapperRef} className={styles.memberList}>
        {loading && members.length === 0 ? (
          <TeamTableLoading />
        ) : (
          members.map((member) => {
            const isFavorite = favorites.includes(member.id);
            const isOwnAgent = ownId === member.id;

            return (
              <TeamTableRow
                key={member.id}
                member={member}
                hasPresenceAccess={hasPresenceAccess}
                hasOrganizationMemberAccess={hasOrganizationMemberAccess}
                showUserModal={showUserModal}
                favorite={
                  showFavorites
                    ? {
                        isFavorite,
                        onToggle: toggleFavorite(isFavorite, member.id),
                      }
                    : undefined
                }
                isOwnAgent={isOwnAgent}
                awayStatuses={awayStatuses}
                isExpanded={expandedMemberId === member.id}
                onToggle={onToggleExpand(member.id)}
                conversations={conversations}
                conversationsLoading={conversationsLoading}
                twilioMakeInternalCall={twilioMakeInternalCall}
                isMicrophoneBlocked={isMicrophoneBlocked}
                hasConversationReplyAccess={hasConversationReplyAccess}
              />
            );
          })
        )}
      </div>
      {pageCount && pageCount > 1 && (
        <FlexBox
          alignItems="center"
          gap="200"
          padding="300"
          className={styles.footer}
        >
          <Pagination
            currentPage={page}
            pageCount={pageCount ?? 1}
            onPageChange={setPage}
          />
          {loading && <Spinner size={16} />}
        </FlexBox>
      )}
    </FlexBox>
  );
};
